// MA-672 — the Journey chapter-signpost speak-text domain.
//
// JourneyPath.tsx's ChapterSignpost renders `chapter.titleKid` with a
// SpeakPromptButton beside it. Those four titles used to be in NO narration
// enumerator, so a child tapping one heard the device fallback voice next to
// recorded stone labels.
//
// ONE SOURCE: JOURNEY_CHAPTERS is the chapters.json manifest the screen reads
// (useJourneyView imports THIS constant, never the JSON directly), and the
// view's `chapter.titleKid` is passed straight through by buildJourneyView.
// scripts/content/narratableStrings.mjs enumerates by building that SAME view
// (journeyChapterTitles below), so the screen and the enumerator cannot drift.
//
// SPOKEN FORM OF "&": "and" — decided ONCE, app-wide, by MA-814's shared
// spokenAmpersand (src/engine/spokenAmpersand.ts), applied at the narration key
// seam (normalizeNarrationText) and the composite's play(). This module no
// longer owns a local rule: the screen requests speakableText(titleKid) and the
// enumerator records normalizeNarrationText(speakableText(titleKid)).
import { buildJourneyView, type JourneyView } from "./journeyView";
import type { Status } from "./skillTreeStatus";
import chaptersManifest from "@/curriculum/data/manifests/chapters.json" with { type: "json" };
import type { Chapter } from "@/types/progression";

export const JOURNEY_CHAPTERS: Chapter[] = (chaptersManifest as unknown as { chapters: Chapter[] }).chapters;

export interface ScreenJourneyViewInput {
  statusById: Record<string, Status | undefined>;
  missionChapterId: string | null;
  recommendedSkillId: string | null;
  /** MA-837: the evidence floors' own skill ids (mission.placementFloorSkillIds). */
  placedFloorSkillIds?: readonly string[];
  /** MA-849: Today's focal is an end-of-runway review rotation pick (mission.reviewRotation). */
  reviewRotationFocal?: boolean;
}

/** The ONE builder of the Journey view the screen renders: binds JOURNEY_CHAPTERS
 *  internally (no `chapters` argument), so useJourneyView and the narration
 *  enumerator cannot feed the view different chapters. */
export function buildScreenJourneyView(input: ScreenJourneyViewInput): JourneyView {
  return buildJourneyView({ chapters: JOURNEY_CHAPTERS, ...input });
}

/** Every chapter titleKid the Journey view emits, in order — read from the
 *  screen's own builder (an empty fold: chapter titles do not depend on stone
 *  status, mission or age; the view emits ALL chapters, always). The optional
 *  `chapters` override exists ONLY for the fifth-chapter mutation proof. */
export function journeyChapterTitles(chapters?: Chapter[]): string[] {
  const view = chapters
    ? buildJourneyView({ chapters, statusById: {}, missionChapterId: null, recommendedSkillId: null })
    : buildScreenJourneyView({ statusById: {}, missionChapterId: null, recommendedSkillId: null });
  return view.chapters.map((c) => c.titleKid);
}
